import { useState, type ReactNode } from 'react';
import { Sparkles, Edit2, RotateCcw, Check } from 'lucide-react';
import { AiSurface } from '@/components/ui/ai-surface';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  SECTION_LABEL,
  SECTION_DESCRIPTION,
  type SectionKey,
  type SynthesisCitation,
  type SynthesisSection,
  type EditSectionInput,
} from '@/lib/api/synthesis';
import { cn } from '@/lib/utils/cn';
import { displayAiText } from '@/lib/utils/ai-text';
import { titleCase } from '@/lib/utils/format';

/** What a section citation's ref_kind is called when the record has no label. */
const SECTION_REF_KIND_LABELS: Record<string, string> = {
  finding: 'Finding',
  domain_rating: 'Domain rating',
  domain_assessment: 'Domain assessment',
  domain: 'Health domain',
  criterion: 'Readiness criterion',
  criterion_score: 'Readiness score',
  readiness_score: 'Readiness score',
  scorecard: 'Scorecard',
  cross_module_reference: 'Linked evidence',
  register_entry: 'Register entry',
  risk: 'Risk',
};

/**
 * The chip's label (C2-31): the record's name the API attached at draft
 * time, else the model's excerpt, else the kind's name. Never the id.
 */
export function sectionCiteLabel(c: SynthesisCitation): string {
  const label = c.label?.trim();
  if (label) return label.length > 60 ? `${label.slice(0, 57)}…` : label;
  const excerpt = c.excerpt?.trim();
  if (excerpt) return excerpt.length > 60 ? `${excerpt.slice(0, 57)}…` : excerpt;
  return SECTION_REF_KIND_LABELS[c.ref_kind] ?? titleCase(c.ref_kind);
}

/**
 * Synthesis section editor (T-0105 / E07-F08).
 *
 * Each section renders in three modes:
 *   - read     — content body in `<AiSurface>` if AI-drafted, normal
 *                paper card if consultant-edited. Edit affordance pinned
 *                top-right.
 *   - editing  — content text + override tag + rationale form; save
 *                calls the PATCH …/synthesis/sections/:id mutation.
 *   - dirty    — section text changed locally but not yet saved.
 *
 * Edit lineage displayed under the title: who edited, when, with the
 * override tag + rationale. AI-drafted (`origin='ai_drafted'`) sections
 * render the AiSurface treatment; consultant-edited sections drop it so
 * the visual contract makes the provenance obvious at a glance per
 * README §6.
 *
 * Citations on the section render as load-bearing `<CitationChip>` (one
 * per `citations[]` entry) and click into the same source modal the chat
 * surface uses. The caller wires the modal — the editor just emits the
 * cite via `onOpenCite`.
 */

/**
 * Any section with the synthesis section's shape. The one Health &
 * readiness report (E20) adds a go-live section whose key is not one of
 * the six synthesis keys, so the key is widened and the caller names it.
 */
export type EditableSection = Omit<SynthesisSection, 'section_key'> & { section_key: string };

export interface SectionEditorProps {
  section: EditableSection;
  /** Heading; defaults to the synthesis label for the section key. */
  title?: string;
  /** Line under the heading; defaults to the synthesis description. */
  description?: string;
  /** Disables edit/save while the report is publishing or read-only. */
  disabled?: boolean;
  /** Called when the user clicks "Save changes" — caller fires the mutation. */
  onSave: (input: EditSectionInput) => Promise<unknown>;
  onOpenCite?: (cite: CitationChipData) => void;
  /**
   * How the saved text reads (T-0245: the report fills its `{{…}}`
   * placeholders from the rollups). Editing always shows the text as
   * stored. Defaults to the text itself.
   */
  renderText?: (text: string) => ReactNode;
  /** A line under the editing textarea. */
  editHint?: ReactNode;
  className?: string;
}

export function SectionEditor({
  section,
  title,
  description,
  disabled,
  onSave,
  onOpenCite,
  renderText,
  editHint,
  className,
}: SectionEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.content_text);
  const [tag, setTag] = useState(section.edit_reason_tag ?? '');
  const [rationale, setRationale] = useState(section.edit_rationale ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const aiDrafted = section.origin === 'ai_drafted';
  const Body = aiDrafted ? AiSurface : 'section';
  const heading = title ?? SECTION_LABEL[section.section_key as SectionKey] ?? section.section_key;
  const blurb = description ?? SECTION_DESCRIPTION[section.section_key as SectionKey];

  function reset() {
    setDraft(section.content_text);
    setTag(section.edit_reason_tag ?? '');
    setRationale(section.edit_rationale ?? '');
    setEditing(false);
    setError(null);
  }

  // A redraft replaces the text under a stable section id, so the editor
  // starts from what the section says now, not from what it said at mount.
  function startEditing() {
    setDraft(section.content_text);
    setTag(section.edit_reason_tag ?? '');
    setRationale(section.edit_rationale ?? '');
    setError(null);
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        content_text: draft,
        citations: section.citations,
        override: {
          tag: tag.trim() || 'consultant_edited',
          rationale: rationale.trim() || 'Consultant override',
        },
      });
      setEditing(false);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Body
      as={aiDrafted ? 'section' : undefined}
      className={cn(
        'rounded-card border p-4',
        aiDrafted ? 'border-ai-surface-border' : 'border-ink-100 bg-paper-0',
        className,
      )}
      aria-labelledby={`synth-section-${section.section_id}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-meta font-semibold uppercase tracking-wide">
            {aiDrafted ? (
              <span className="inline-flex items-center gap-1 text-ai-ink">
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                AI-drafted
              </span>
            ) : (
              <span className="text-ink-700">Consultant-edited</span>
            )}
          </div>
          <h3
            id={`synth-section-${section.section_id}`}
            className={cn(
              'mt-1 text-h3 font-semibold',
              aiDrafted ? 'text-ink-900' : 'text-ink-900',
            )}
          >
            {heading}
          </h3>
          {blurb ? <p className="mt-0.5 text-meta text-ink-500">{blurb}</p> : null}
          {section.edited_at ? <EditLineage section={section} /> : null}
        </div>
        <div className="flex items-center gap-2">
          <ProvenanceChip
            origin={section.origin === 'ai_drafted' ? 'ai_proposed' : 'consultant_edited'}
            lineage={{
              confirmed_by: section.edited_by_user_id ?? undefined,
              confirmed_at: section.edited_at?.slice(0, 10),
              was: section.origin === 'consultant_edited' ? 'ai_proposed' : undefined,
            }}
          />
          {!editing ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={startEditing}
            >
              <Edit2 className="h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
          ) : null}
        </div>
      </header>

      <div className="mt-3">
        {editing ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor={`section-content-${section.section_id}`} className="text-meta">
                Section content
              </Label>
              <textarea
                id={`section-content-${section.section_id}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.max(6, Math.min(20, draft.split('\n').length + 2))}
                className="mt-1 w-full rounded border border-ink-200 bg-paper-0 p-2 text-body-sm leading-relaxed text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              />
              {editHint ? <p className="mt-1 text-meta text-ink-500">{editHint}</p> : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor={`section-tag-${section.section_id}`} className="text-meta">
                  Override tag
                </Label>
                <Input
                  id={`section-tag-${section.section_id}`}
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="consultant_edited"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor={`section-rationale-${section.section_id}`} className="text-meta">
                  Rationale
                </Label>
                <Input
                  id={`section-rationale-${section.section_id}`}
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Why the edit"
                  className="mt-1"
                />
              </div>
            </div>
            {error ? <ErrorMessage error={error} /> : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={saving} onClick={handleSave}>
                <Check className="h-4 w-4" aria-hidden="true" />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={saving} onClick={reset}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Revert
              </Button>
            </div>
          </div>
        ) : (
          <p
            className={cn(
              'whitespace-pre-wrap text-body leading-relaxed',
              aiDrafted ? 'text-ink-700' : 'text-ink-900',
            )}
          >
            {section.content_text ? (
              renderText ? (
                renderText(displayAiText(section.content_text))
              ) : (
                displayAiText(section.content_text)
              )
            ) : (
              <span className="italic text-ink-500">
                Empty section — open editor to draft content.
              </span>
            )}
          </p>
        )}
      </div>

      {!editing && section.citations.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5 border-t border-ink-100 pt-3">
          {section.citations.map((c, i) => {
            const cite: CitationChipData = {
              id: `${section.section_id}-${i}`,
              doc_label: sectionCiteLabel(c),
              section: null,
              page: null,
              confidence: 1,
              status: 'ok',
            };
            return (
              <li key={`${section.section_id}-${i}`}>
                <CitationChip cite={cite} onOpen={() => onOpenCite?.(cite)} />
              </li>
            );
          })}
        </ul>
      ) : null}
    </Body>
  );
}

/**
 * Edit lineage line: "Edited by user-id on YYYY-MM-DD · tag: <tag>".
 * Shown under the section title when the section has been edited. Kept
 * permissive — any subset of the lineage fields renders gracefully.
 */
function EditLineage({ section }: { section: EditableSection }) {
  const when = section.edited_at?.slice(0, 10);
  return (
    <p className="mt-1 text-meta text-ink-500">
      Edited
      {section.edited_by_user_id ? ` by ${section.edited_by_user_id}` : ''}
      {when ? ` on ${when}` : ''}
      {section.edit_reason_tag ? ` · tag: ${section.edit_reason_tag}` : ''}
      {section.edit_rationale ? ` · ${section.edit_rationale}` : ''}
    </p>
  );
}
